import { captureException } from '@sentry/browser'
import type { PropsWithChildren } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from '~/utils/storage'
import { attemptParseJSON } from '../../utils'
import { useMyInfo } from '../apollo/auth'
import type { UserPreferences } from '../apollo/preference'
import {
  executeSetPreferences,
  updateGetMyPreferences,
  useMyPreferences,
} from '../apollo/preference'
import {
  TSUBAKI_PROMPT_HELPER_VALUE,
  TSUBAKI2_PROMPT_HELPER_VALUE,
} from '../constants/preference'
import {
  useContentSafetyPreferences,
  useUpdateContentSafetyPreference,
} from '../orpc/contentSafetyPreference'

export const PREFERENCE_STORAGE_KEY_PREFIX = 'preferences::'

export const RICH_TEXT_TRIGGER_WORDS_ENABLE_USERID = '1965941516666329283'

export function getRichTextTriggerWordsDefaultByUserId(
  userId: string | undefined,
): boolean {
  if (!userId) return false
  try {
    return BigInt(userId) > BigInt(RICH_TEXT_TRIGGER_WORDS_ENABLE_USERID)
  } catch {
    return false
  }
}

const PREFERENCES = [
  'showNsfw',
  'blurNsfw',
  'promoEmail',
  'ageVerificationStatus',
  'experienceLevel',
  'disableAutoPromptHelper',
  /**
   * @deprecated
   */
  'allowPromptHelperSwitchForTsubaki',
  'tsubakiPromptHelper',
  'tsubaki2PromptHelper',
]

export function getPreferenceFromLocalStorage(key: string) {
  if (typeof window === 'undefined') {
    return undefined
  }
  const storageKey = `${PREFERENCE_STORAGE_KEY_PREFIX}${key}`
  const storageValue = getLocalStorage(storageKey)
  if (!storageValue) {
    return undefined
  }

  return attemptParseJSON(storageValue)
}

function syncPreferenceValueToLocalStorage(key: string, value: any) {
  if (typeof window === 'undefined') {
    return
  }
  const storageKey = `${PREFERENCE_STORAGE_KEY_PREFIX}${key}`
  setLocalStorage(storageKey, JSON.stringify(value))
}

export function resetLocalPreferences() {
  if (typeof window === 'undefined') {
    return
  }
  for (const key of PREFERENCES) {
    removeLocalStorage(`${PREFERENCE_STORAGE_KEY_PREFIX}${key}`)
  }
}

export interface PreferenceOption<K extends keyof UserPreferences> {
  localStorageGetter?: () => UserPreferences[K]
  localStorageSetter?: (value: UserPreferences[K]) => void
}

export interface PreferenceContextType {
  preference?: UserPreferences
  updateValue: <K extends keyof UserPreferences>(
    key: K,
    value: NonNullable<UserPreferences[K]> | null,
    opts?: PreferenceOption<K>,
  ) => Promise<void>
}

export const PreferenceContext = createContext<PreferenceContextType>({
  updateValue: async () => {},
})

export function UserPreferenceProvider(props: PropsWithChildren) {
  const { data: preference, refetch } = useMyPreferences()
  const { t } = useTranslation(['user'])

  // Delay refetch to avoid a race condition where the server hasn't
  // propagated the mutation result yet and returns the stale value.
  // Each update cancels the previous pending refetch and schedules a new one,
  // so the refetch always fires 10000ms after the last update.
  const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (refetchTimeoutRef.current) clearTimeout(refetchTimeoutRef.current)
    }
  }, [])

  const updateValue = useCallback(
    async <K extends keyof UserPreferences>(
      key: K,
      value: NonNullable<UserPreferences[K]> | null,
      opts?: PreferenceOption<K>,
    ) => {
      const lastLocalStorageValue = opts?.localStorageGetter?.()

      // After using null to unset the preference on server side
      // The preference becomes undefined
      // This will simulate this behaviour on local cache and localStorage
      const normalizedLocalValue = value ?? undefined

      try {
        await executeSetPreferences(
          { value: { [key]: value } },
          {
            optimisticResponse: { setPreferences: null },
            update() {
              updateGetMyPreferences({ [key]: normalizedLocalValue })
            },
          },
        )
        opts?.localStorageSetter?.(normalizedLocalValue)

        if (refetchTimeoutRef.current) clearTimeout(refetchTimeoutRef.current)
        refetchTimeoutRef.current = setTimeout(refetch, 10000)
      } catch (err) {
        // Rollback and warn (Apollo auto-rolls back cache changes from update() so only localStorage needs to be rolled back manually)
        opts?.localStorageSetter?.(lastLocalStorageValue)
        captureException(err, { tags: { preference: key } })
        toast.error(t('user:profile.preference.update-failed'))
      }
    },
    [t, refetch],
  )
  const contextValue: PreferenceContextType = useMemo(
    () => ({
      preference,
      updateValue,
    }),
    [preference, updateValue],
  )
  return (
    <PreferenceContext.Provider value={contextValue}>
      <RichTextTriggerWordsDefaultSyncer />
      {props.children}
    </PreferenceContext.Provider>
  )
}

const createUsePreferenceHook = <
  K extends keyof UserPreferences,
  DefaultV extends UserPreferences[K],
>(
  key: K,
  defaultValue: DefaultV,
  opts?: PreferenceOption<K>,
) => {
  return () => {
    const { preference, updateValue } = useContext(PreferenceContext)

    const value =
      preference?.[key] ?? opts?.localStorageGetter?.() ?? defaultValue

    useEffect(() => {
      if (preference?.[key]) {
        opts?.localStorageSetter?.(preference?.[key])
      }
    }, [preference, key])

    const [loading, setValue] = useLoadingCallback(
      // passing null here will unset the preference on server side
      // passing undefined here will do nothing, so we forbid this
      async (value: NonNullable<UserPreferences[K]> | null) => {
        await updateValue(key, value, opts)
      },
      [key, opts],
    )

    // TODO: loading is ambiguous here, use a better name so we can tell this is for loading state of setValue
    return [value, setValue, loading] as const
  }
}

/**
 * Discriminated config for `createContentSafetyBooleanHook`. The
 * `apiKey` literal locks down which `trueValue`/`falseValue` strings
 * are accepted, mirroring the contract's
 * `updateContentSafetyPreference` discriminated-union input shape so a
 * typo in either value is a compile error rather than a runtime
 * validation failure. Addresses Copilot review on #11227 — the previous
 * `as Parameters<typeof mutateAsync>[0]` cast hid this relationship.
 */
type ContentSafetyBooleanConfig =
  | {
      field: 'showNsfw'
      apiKey: 'show-nsfw'
      trueValue: 'show'
      falseValue: 'hide'
      defaultValue: boolean
    }
  | {
      field: 'blurNsfw'
      apiKey: 'blur-nsfw'
      trueValue: 'blur'
      falseValue: 'show'
      defaultValue: boolean
    }

const createContentSafetyBooleanHook = (config: ContentSafetyBooleanConfig) => {
  return () => {
    const { t } = useTranslation(['user'])
    const { data } = useContentSafetyPreferences()
    const { mutateAsync, isPending } = useUpdateContentSafetyPreference()
    const apiValue = data?.[config.field]?.value
    const value =
      apiValue == null ? config.defaultValue : apiValue === config.trueValue

    const [loading, setValue] = useLoadingCallback(
      async (v: boolean) => {
        try {
          // Build the mutation input by branching on `apiKey` so each
          // case lands in the matching arm of the contract's
          // discriminated union — no `as` cast required.
          if (config.apiKey === 'show-nsfw') {
            await mutateAsync({
              key: 'show-nsfw',
              value: v ? config.trueValue : config.falseValue,
            })
          } else {
            await mutateAsync({
              key: 'blur-nsfw',
              value: v ? config.trueValue : config.falseValue,
            })
          }
        } catch (err) {
          captureException(err, { tags: { preference: config.field } })
          toast.error(t('user:profile.preference.update-failed'))
        }
      },
      [mutateAsync],
    )

    return [value, setValue, loading || isPending] as const
  }
}

export const useShowNsfw = createContentSafetyBooleanHook({
  field: 'showNsfw',
  apiKey: 'show-nsfw',
  trueValue: 'show',
  falseValue: 'hide',
  defaultValue: false,
})

export const useBlurNsfw = createContentSafetyBooleanHook({
  field: 'blurNsfw',
  apiKey: 'blur-nsfw',
  trueValue: 'blur',
  falseValue: 'show',
  defaultValue: true,
})
export const usePromoEmailPref = createUsePreferenceHook('promoEmail', false)
export const useDisableAutoPromptHelperPref = createUsePreferenceHook(
  'disableAutoPromptHelper',
  false,
)

export const TSUBAKI_PROMPT_HELPER = 'tsubakiPromptHelper'

export { TSUBAKI_PROMPT_HELPER_VALUE } from '../constants/preference'
export const useTsubakiPromptHelperPref = createUsePreferenceHook(
  TSUBAKI_PROMPT_HELPER,
  TSUBAKI_PROMPT_HELPER_VALUE.autoEnable,
  {
    localStorageGetter: () =>
      getPreferenceFromLocalStorage(TSUBAKI_PROMPT_HELPER),
    localStorageSetter: value =>
      syncPreferenceValueToLocalStorage(TSUBAKI_PROMPT_HELPER, value),
  },
)

export const TSUBAKI2_PROMPT_HELPER = 'tsubaki2PromptHelper'

export { TSUBAKI2_PROMPT_HELPER_VALUE } from '../constants/preference'
export const useTsubaki2PromptHelperPref = createUsePreferenceHook(
  TSUBAKI2_PROMPT_HELPER,
  TSUBAKI2_PROMPT_HELPER_VALUE.autoEnable,
  {
    localStorageGetter: () =>
      getPreferenceFromLocalStorage(TSUBAKI2_PROMPT_HELPER),
    localStorageSetter: value =>
      syncPreferenceValueToLocalStorage(TSUBAKI2_PROMPT_HELPER, value),
  },
)

export const PROMPT_STYLE = 'promptStyle'
export enum PROMPT_STYLE_VALUE {
  preciseTags = 'precise-tags',
  naturalLanguage = 'natural-language',
}
export const usePromptStylePref = createUsePreferenceHook(
  PROMPT_STYLE,
  PROMPT_STYLE_VALUE.naturalLanguage,
  // Save this in local storage to be used by promptHelperModeAtom (for its default value)
  // since promptHelperModeAtom does not have access to preference hooks
  {
    localStorageGetter: () => getPreferenceFromLocalStorage(PROMPT_STYLE),
    localStorageSetter: value =>
      syncPreferenceValueToLocalStorage(PROMPT_STYLE, value),
  },
)

export const usePushPrefs = () =>
  ['FOLLOW', 'LIKE', 'COMMENT', 'TASK', 'NEWS'].map(
    pref =>
      [
        `push-${pref}`,
        createUsePreferenceHook(`push-${pref}`, true)(),
      ] as const,
  )
export const useAgeVerificationStatus = () => {
  const { t } = useTranslation(['user'])
  const { data, refetch } = useContentSafetyPreferences()
  const { mutateAsync, isPending } = useUpdateContentSafetyPreference()

  const value = data?.ageVerified?.value ?? undefined

  const [loading, setValue] = useLoadingCallback(
    async (v: 'adult' | 'minor' | null) => {
      try {
        if (v === null) {
          // Unsetting not supported by the new oRPC API; fall back to GraphQL
          await executeSetPreferences(
            { value: { ageVerificationStatus: null } },
            {},
          )
          await refetch()
          return
        }
        await mutateAsync({ key: 'age-verified', value: v })
      } catch (err) {
        captureException(err, { tags: { preference: 'ageVerificationStatus' } })
        toast.error(t('user:profile.preference.update-failed'))
      }
    },
    [mutateAsync, refetch],
  )

  return [value, setValue, loading || isPending] as const
}

export {
  genderOptions,
  type Gender,
  promptStyleOptions,
  type PromptStyle,
  type ExperienceLevel,
} from '../constants/preference'

export const useRichTextTriggerWords = createUsePreferenceHook(
  'richTextTriggerWords',
  undefined,
)

function RichTextTriggerWordsDefaultSyncer() {
  const { data: userInfo } = useMyInfo()
  const { preference, updateValue } = useContext(PreferenceContext)
  const hasSynced = useRef(false)

  useEffect(() => {
    if (hasSynced.current) return
    if (!userInfo?.id) return
    // Wait for preference data to load before syncing
    if (preference === undefined) return
    // Only sync if richTextTriggerWords is undefined (not explicitly set by user)
    if (preference.richTextTriggerWords !== undefined) return

    const defaultValue = getRichTextTriggerWordsDefaultByUserId(userInfo.id)
    hasSynced.current = true
    updateValue('richTextTriggerWords', defaultValue)
  }, [userInfo?.id, preference, updateValue])

  return null
}
